import type { EstadoPase } from '../api/ops/pasesCliente';
import type { EstadoChip } from '../componentes/Chip';

/**
 * Lógica de la página del pase (6.6) y de «Mis pases» (6.7), sin React: fechas en la zona del negocio, el código en
 * grupos de 4, el chip de cada estado («misma palabra, mismo color», componentes.md §7) y «Pase n de N» (G414, G785).
 */

const mayuscula = (t: string) => (t === '' ? t : t.charAt(0).toUpperCase() + t.slice(1));
const sinPunto = (t: string) => t.replace(/\.$/, '');

function partes(fecha: Date, zona: string, opciones: Intl.DateTimeFormatOptions): Record<string, string> {
    let f: Intl.DateTimeFormat;
    try {
        f = new Intl.DateTimeFormat('es-ES', { ...opciones, timeZone: zona });
    } catch {
        f = new Intl.DateTimeFormat('es-ES', opciones);
    }
    return Object.fromEntries(f.formatToParts(fecha).map((p) => [p.type, p.value]));
}

/** `2026-10-17` (fecha local del negocio) como instante a mediodía UTC, para escribirla sin que cambie de día. */
const diaLocal = (fecha: string) => new Date(`${fecha}T12:00:00Z`);

/** «Sáb 17 oct». */
export function diaCorto(fecha: Date, zona: string): string {
    const p = partes(fecha, zona, { weekday: 'short', day: 'numeric', month: 'short' });
    return `${mayuscula(sinPunto(p.weekday ?? ''))} ${p.day ?? ''} ${sinPunto(p.month ?? '')}`;
}

/** «Sábado 17 de octubre». */
export function diaLargo(fecha: Date, zona: string): string {
    const p = partes(fecha, zona, { weekday: 'long', day: 'numeric', month: 'long' });
    return `${mayuscula(p.weekday ?? '')} ${p.day ?? ''} de ${p.month ?? ''}`;
}

/** «21:30» en la zona del negocio. */
export function horaEn(iso: string, zona: string): string {
    const p = partes(new Date(iso), zona, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return `${p.hour ?? ''}:${p.minute ?? ''}`;
}

interface ConFecha {
    valid_from: string;
    valid_to: string;
    timezone: string;
    local_date?: string;
    local_time?: string;
    previous_valid_from?: string | null;
}

/** Día y hora del pase: la fecha local que da la API (`local_date`, `local_time`) o, si no, `valid_from` en su zona. */
export function cuando(p: ConFecha): { corto: string; largo: string; hora: string } {
    const dia = p.local_date === undefined ? new Date(p.valid_from) : diaLocal(p.local_date);
    const zona = p.local_date === undefined ? p.timezone : 'UTC';
    return { corto: diaCorto(dia, zona), largo: diaLargo(dia, zona), hora: p.local_time ?? horaEn(p.valid_from, p.timezone) };
}

/** «De 21:00 a 00:30»: la ventana para entrar. */
export const ventana = (p: ConFecha) => `De ${horaEn(p.valid_from, p.timezone)} a ${horaEn(p.valid_to, p.timezone)}`;

/** «Sáb 10 oct · 21:00»: la fecha de antes si cambió (G17, G785); null si nunca cambió. */
export function fechaAnterior(p: ConFecha): string | null {
    if (p.previous_valid_from == null) {
        return null;
    }
    return `${diaCorto(new Date(p.previous_valid_from), p.timezone)} · ${horaEn(p.previous_valid_from, p.timezone)}`;
}

/** «7KQ2 M9XH 4TPC». */
export const codigoEnGrupos = (c: string) => c.replace(/(.{4})(?=.)/g, '$1 ');

/** «Pase 1 de 2» solo si el acceso tiene más de uno (G414, G785). */
export function paseNDeN(p: { seq?: number | null; seq_total?: number | null }): string | null {
    return p.seq != null && p.seq_total != null && p.seq_total > 1 ? `Pase ${String(p.seq)} de ${String(p.seq_total)}` : null;
}

/** Chip del estado; el válido sin chip. Un válido con la fecha movida, «Fecha cambiada». */
export function chipDelPase(estado: EstadoPase, cambiada: boolean): readonly [string, EstadoChip] | null {
    switch (estado) {
        case 'active':
            return cambiada ? ['Fecha cambiada', 'pending'] : null;
        case 'inside':
            return ['Usado', 'neutral'];
        case 'voided':
            return ['Anulado', 'problem'];
        case 'expired':
            return ['Caducado', 'problem'];
    }
}

/** Se puede enseñar en la puerta: válido (aún sin llegada). */
export const sePuedeUsar = (estado: EstadoPase) => estado === 'active';

/**
 * Por dónde se ha reenviado un pase (G1056, G1077; C.56), con `ResendAccepted.channel` y `destination_hint` (enmascarado):
 * «Por WhatsApp al +34 6** *** 678», «Por email a a***@gmail.com», «Por Instagram» o «Por Telegram» (estos dos no tienen
 * destino que enseñar). Con `destination_hint` nulo, «Por WhatsApp» o «Por email» sin destino (G1182). Sin canal conocido, null.
 */
export function porDondeTeLlego(canal: string | undefined, destino: string | null | undefined, idioma: 'es' | 'en' = 'es'): string | null {
    const en = idioma === 'en';
    switch (canal) {
        case 'whatsapp':
            // G1182: con `destination_hint` nulo, «Por WhatsApp» / «Por email» sin destino.
            return destino ? (en ? `By WhatsApp to ${destino}` : `Por WhatsApp al ${destino}`) : en ? 'By WhatsApp' : 'Por WhatsApp';
        case 'email':
            return destino ? (en ? `By email to ${destino}` : `Por email a ${destino}`) : en ? 'By email' : 'Por email';
        case 'instagram':
            return en ? 'By Instagram' : 'Por Instagram';
        case 'telegram':
            return en ? 'By Telegram' : 'Por Telegram';
        default:
            return null;
    }
}

/**
 * El aviso del reenvío en la página del pase (6.6) y en «Mis pases» (6.7), el mismo en las dos (C.56): «Te lo hemos vuelto
 * a mandar. Por WhatsApp al +34 6** *** 678, por donde te llegó. Puedes pedirlo 2 veces más esta hora.»
 */
export function textoReenviado(canal: string | undefined, destino: string | null | undefined, quedan: number | undefined): string {
    const via = porDondeTeLlego(canal, destino);
    return `${via === null ? 'Te lo hemos vuelto a mandar por donde te llegó.' : `Te lo hemos vuelto a mandar. ${via}, por donde te llegó.`}${quedanReenvios(quedan)}`;
}

/** `409 no_contact` al reenviar (G1076, G1080, G1279): el cliente no tiene ningún email ni teléfono al que mandarlo (6.6 y 6.7). */
export const REENVIO_SIN_CONTACTO = 'No podemos reenviártelo: no tenemos ningún email ni teléfono tuyo al que mandarlo. Guárdalo en la cartera del móvil para tenerlo a mano.';

/** « Puedes pedirlo 2 veces más esta hora.» con lo que queda (`remaining_this_hour`). */
export function quedanReenvios(quedan: number | undefined): string {
    if (quedan === undefined) {
        return '';
    }
    return quedan <= 0 ? ' Ya no puedes pedirlo más esta hora.' : ` Puedes pedirlo ${quedan === 1 ? '1 vez más' : `${String(quedan)} veces más`} esta hora.`;
}

interface DeUnAcceso {
    id: string;
    access_group_id?: string | null;
    passable_type?: string;
    passable_id?: string | null;
}

/** Clave del acceso de un pase (G785): su grupo o, sin grupo, el acreditable; sin nada, el propio pase. */
export function claveDeAcceso(p: DeUnAcceso): string {
    if (p.access_group_id != null) {
        return `grupo:${p.access_group_id}`;
    }
    return p.passable_type !== undefined && p.passable_id != null ? `${p.passable_type}:${p.passable_id}` : `pase:${p.id}`;
}

export interface FilaDeAcceso<T> {
    pase: T;
    /** En la primera fila de un acceso con 2 pases o más, cuántos tiene; si no, null. */
    cabecera: number | null;
    /** Si la fila va dentro de un acceso con 2 pases o más: dice de quién es y «Pase n de N». */
    enGrupo: boolean;
}

/**
 * «Mis pases» agrupado por acceso (6.7, G785): los pases del mismo acceso van juntos, donde sale el primero, bajo una
 * cabecera con su nombre, el día y «N pases»; un acceso con un solo pase va como siempre.
 */
export function agruparPorAcceso<T extends DeUnAcceso>(pases: readonly T[]): FilaDeAcceso<T>[] {
    const grupos = new Map<string, T[]>();
    for (const p of pases) {
        const k = claveDeAcceso(p);
        grupos.set(k, [...(grupos.get(k) ?? []), p]);
    }
    return [...grupos.values()].flatMap((g) => g.map((pase, i) => ({ pase, cabecera: g.length > 1 && i === 0 ? g.length : null, enGrupo: g.length > 1 })));
}
